import { useState } from 'react'
import { Link } from 'react-router-dom'
import { Mail, ArrowRight, Check, Loader2 } from 'lucide-react'
import { supabase } from '../../utils/supabase'

export default function EmailCapture() {
    const [email, setEmail] = useState('')
    const [status, setStatus] = useState('idle') // idle, loading, success, error
    const [errorMessage, setErrorMessage] = useState('')

    const validateEmail = (email) => {
        const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
        return regex.test(email)
    }

    const handleSubmit = async (e) => {
        e.preventDefault()

        if (!validateEmail(email)) {
            setStatus('error')
            setErrorMessage('Please enter a valid email address')
            return
        }

        setStatus('loading')
        setErrorMessage('')

        try {
            const normalizedEmail = email.toLowerCase().trim()
            const timestamp = new Date().toISOString()

            // Save to both tables for backward compatibility and newsletter sending
            const [waitlistResult, subscriberResult] = await Promise.allSettled([
                // Original email_waitlist table
                supabase
                    .from('email_waitlist')
                    .insert([{
                        email: normalizedEmail,
                        source: 'landing_page',
                        subscribed_at: timestamp
                    }]),
                // New newsletter_subscribers table
                supabase
                    .from('newsletter_subscribers')
                    .insert([{
                        email: normalizedEmail,
                        source: 'landing_page',
                        status: 'active'
                    }])
            ])

            // Check for errors (ignore duplicate key errors)
            const hasNonDuplicateError = [waitlistResult, subscriberResult].some(
                result => result.status === 'rejected' ||
                    (result.value?.error && result.value.error.code !== '23505')
            )

            if (hasNonDuplicateError) {
                throw new Error('Subscription failed')
            }

            setStatus('success')
            setEmail('')
        } catch (error) {
            console.error('Email capture error:', error)
            setStatus('error')
            setErrorMessage('Something went wrong. Please try again.')
        }
    }

    return (
        <section id="newsletter" className="relative py-24 sm:py-32 overflow-hidden">
            {/* Background */}
            <div className="absolute inset-0 bg-gradient-to-br from-primary-950 via-slate-950 to-accent-950" />

            {/* Decorative Elements */}
            <div className="absolute top-0 left-1/4 w-96 h-96 bg-primary-500/20 rounded-full blur-3xl" />
            <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-accent-500/20 rounded-full blur-3xl" />

            {/* Content */}
            <div className="relative z-10 section-container">
                <div className="max-w-2xl mx-auto text-center">
                    {/* Badge */}
                    <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full glass-card mb-6">
                        <Mail className="w-4 h-4 text-primary-400" />
                        <span className="text-sm font-medium text-white/80">Stay Updated</span>
                    </div>

                    {/* Heading */}
                    <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4 text-balance">
                        Get Writing Tips & {' '}
                        <span className="gradient-text">Exclusive Updates</span>
                    </h2>

                    <p className="text-lg text-white/60 mb-8 text-balance">
                        Join our newsletter for writing tips, feature updates, and exclusive offers.
                        Be the first to know about new AI-powered features.
                    </p>

                    {/* Form */}
                    <form onSubmit={handleSubmit} className="relative max-w-md mx-auto">
                        <div className="flex flex-col sm:flex-row gap-3">
                            <div className="relative flex-grow">
                                <input
                                    type="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    placeholder="Enter your email"
                                    className="input-field pl-12 pr-4"
                                    disabled={status === 'loading' || status === 'success'}
                                />
                                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/40" />
                            </div>

                            <button
                                type="submit"
                                disabled={status === 'loading' || status === 'success'}
                                className="btn-primary whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {status === 'loading' && (
                                    <>
                                        <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                                        Joining...
                                    </>
                                )}
                                {status === 'success' && (
                                    <>
                                        <Check className="w-5 h-5 mr-2" />
                                        You're In!
                                    </>
                                )}
                                {(status === 'idle' || status === 'error') && (
                                    <>
                                        Subscribe
                                        <ArrowRight className="w-5 h-5 ml-2" />
                                    </>
                                )}
                            </button>
                        </div>

                        {/* Status Messages */}
                        {status === 'error' && (
                            <p className="mt-3 text-sm text-red-400">
                                {errorMessage}
                            </p>
                        )}

                        {status === 'success' && (
                            <p className="mt-3 text-sm text-green-400 flex items-center justify-center gap-2">
                                <Check className="w-4 h-4" />
                                Thanks for subscribing! Check your inbox.
                            </p>
                        )}
                    </form>

                    {/* Trust badges */}
                    <div className="mt-8 flex flex-wrap items-center justify-center gap-6 text-white/40 text-sm">
                        <div className="flex items-center gap-2">
                            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                            </svg>
                            No spam, ever
                        </div>
                        <div className="flex items-center gap-2">
                            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
                                <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
                            </svg>
                            Unsubscribe anytime
                        </div>
                        <div className="flex items-center gap-2">
                            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                            </svg>
                            5,000+ subscribers
                        </div>
                    </div>

                    {/* Browse Past Issues Link */}
                    <div className="mt-6">
                        <Link
                            to="/newsletter"
                            className="text-sm text-primary-400 hover:text-primary-300 transition-colors"
                        >
                            📚 Browse past newsletter issues →
                        </Link>
                    </div>
                </div>
            </div>
        </section>
    )
}
